import { ApolloClient, from, HttpLink, InMemoryCache, ApolloLink, split, Observable } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { createClient } from "graphql-ws";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { NetworkConfig } from "./NetworkConfig";
import { getRoleKey } from "./User";
import { AuthStore } from "src/stores/AuthStore";
import { Route } from "src/config/routes";

export const createApolloClient = (authStore: AuthStore): ApolloClient<any> => {
    const httpLink = new HttpLink({
        uri: NetworkConfig.graphqlUrl
    });

    const wsLink = new GraphQLWsLink(
        createClient({
            url: NetworkConfig.graphqlWssUrl,
            connectionParams: () => {
                return {
                    reconnect: true,
                    headers: {
                        // Für den Ws-Link wird hier das Auth direkt gesetzt.
                        Authorization: authStore.token ? `Bearer ${authStore.token}` : "",
                        "x-hasura-role": getRoleKey(authStore.user?.role)
                    }
                };
            }
        })
    );

    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === "OperationDefinition" && definition.operation === "subscription";
        },
        wsLink, // use wsLink for subscriptions only
        httpLink // use httpLink as default
    );

    const queryPerformanceLink = new ApolloLink((operation, forward) => {
        const definition = getMainDefinition(operation.query);
        const transaction = Sentry.startTransaction({
            name: operation.operationName,
            op:
                definition.kind === "OperationDefinition" && definition.operation === "subscription"
                    ? "subscription"
                    : "query",
            data: operation.variables
        });

        operation.setContext({ transaction });

        return forward(operation).map((data) => {
            const transaction = operation.getContext().transaction;
            transaction.finish();
            return data;
        });
    });

    const authLink = setContext((_, { headers }) => {
        let manuallySetHasuraRole = undefined;

        if (headers && headers.hasOwnProperty("x-hasura-role")) {
            manuallySetHasuraRole = headers["x-hasura-role"];
        }

        const headerObject = authStore.token
            ? {
                  ...headers,
                  Authorization: authStore.token ? `Bearer ${authStore.token}` : "",
                  "x-hasura-role":
                      manuallySetHasuraRole ?? authStore.currentlyUsedRole ?? getRoleKey(authStore.user?.role)
              }
            : { ...headers };

        return {
            headers: headerObject
        };
    });

    // Used when networkError occurs
    const retryLink = new RetryLink({
        attempts: {
            max: 3
        }
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        if (graphQLErrors) {
            for (const err of graphQLErrors) {
                switch (err.extensions.code) {
                    case "invalid-jwt":
                    case "invalid-headers":
                        if (authStore.token && authStore.refreshToken) {
                            return new Observable((observer) => {
                                authStore.authClient
                                    .refresh(authStore.refreshToken)
                                    .then((reloginSuccess) => {
                                        if (reloginSuccess && authStore.token) {
                                            console.log("Relogin durch ApolloClient mit refreshToken erfolgreich");
                                            operation.setContext(({ headers = {} }: any) => ({
                                                headers: {
                                                    ...headers,
                                                    authorization: `${authStore.tokenType} ${authStore.token}`
                                                }
                                            }));
                                        } else {
                                            console.warn(
                                                "Logout weil relogin mit refreshToken durch ApolloClient fehlgeschlagen ist"
                                            );
                                            authStore.setToken(undefined);
                                            window.location.href = `${Route.logoutSilent}`;
                                        }
                                    })
                                    .then(() => {
                                        console.log(`Retrying Operation ${operation.operationName}`);
                                        const subscriber = {
                                            next: observer.next.bind(observer),
                                            error: observer.error.bind(observer),
                                            complete: observer.complete.bind(observer)
                                        };
                                        forward(operation).subscribe(subscriber);
                                    })
                                    .catch((error) => {
                                        observer.error(error);
                                    });
                            });
                        }
                        break;
                    default:
                        console.error(
                            `[GraphQL error] Code: ${err.extensions?.code}; Message: ${err.message}; operationName: ${operation.operationName}`
                        );
                        break;
                }
            }
        }
        if (networkError) {
            console.error(`[Network error]: ${networkError}`);
        }
    });

    const client = new ApolloClient({
        link: from([queryPerformanceLink, errorLink, authLink, retryLink, splitLink]),
        cache: new InMemoryCache({
            typePolicies: {
                file_v_manager_files_overview: {
                    keyFields: ["id", "category"]
                },
                file_v_owner_files_overview: {
                    keyFields: ["id", "category"]
                },
                file_v_coowner_files_overview: {
                    keyFields: ["id", "category"]
                }
            }
        })
    });

    return client;
};
